import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import startCase from 'lodash/startCase';
import lowerCase from 'lodash/lowerCase';
import capitalize from 'lodash/capitalize';
import classNames from 'classnames';
import { track, ReferrerQueryString } from '@fff-web/fff-analytics';

import env from 'pages/MyAccount/utils/env';
import { getUserSubscriptionStatus } from 'pages/MyAccount/utils/user';
import { SUBSCRIPTION_STATUS } from 'pages/constants';
import Button from 'pages/MyAccount/shared-components/Button/Button';
import { MY_ACCOUNT_EXPERIENCE } from '../utils/segment';
import ProfileImage from './ProfileImage';
import RemainingBoxes from '../RemainingBoxes/RemainingBoxes';
import styles from './Profile.module.css';

const CANCELED_REACTIVATE_URL = `https://${env.hostname}/reactivate-today`;
const EXPIRED_REACTIVATE_URL = `https://${env.hostname}/reactivate-my-membership`;
const UPGRADE_CONFIRMATION_URL = `https://${env.hostname}
/upgrade-confirmation?referrer=${ReferrerQueryString.myAccount}`;

const REACTIVATE_TRACKING_DATA = {
  name: 'Reactivation Started',
  ctaName: 'Reactivate Now',
  canceledMemberType: 'Canceled Member',
  expiredMemberType: 'Expired Member',
};

const UPGRADE_TRACKING_DATA = {
  name: 'Upgrade Started',
};

const handleClickCancelledReactivate = (subscription) => {
  track({
    trackName: REACTIVATE_TRACKING_DATA.name,
    properties: {
      cta_name: REACTIVATE_TRACKING_DATA.ctaName,
      type: REACTIVATE_TRACKING_DATA.canceledMemberType,
      experience: MY_ACCOUNT_EXPERIENCE,
      reactivation_type: `Canceled ${capitalize(subscription)}`,
    },
  }).finally(() => window.location.assign(CANCELED_REACTIVATE_URL));
};

const handleClickExpiredReactivate = () => {
  track({
    trackName: REACTIVATE_TRACKING_DATA.name,
    properties: {
      cta_name: REACTIVATE_TRACKING_DATA.ctaName,
      type: REACTIVATE_TRACKING_DATA.expiredMemberType,
      experience: MY_ACCOUNT_EXPERIENCE,
      reactivation_type: 'Expired',
    },
  }).finally(() => window.location.assign(EXPIRED_REACTIVATE_URL));
};

const handleClickUpgrade = (cta) => {
  track({
    trackName: UPGRADE_TRACKING_DATA.name,
    properties: {
      cta_name: cta,
      experience: MY_ACCOUNT_EXPERIENCE,
    },
  }).finally(() => window.location.assign(UPGRADE_CONFIRMATION_URL));
};

const SUBSCRIPTION_COPY = {
  seasonal: {
    text: 'Get the Most Out of Your Membership',
    CTA: 'UPGRADE TO ANNUAL',
    onClick: () => handleClickUpgrade('UPGRADE TO ANNUAL'),
  },
  trial: {
    text: `Let's Make it Official!`,
    CTA: 'ACTIVATE MY MEMBERSHIP',
    onClick: () => {
      window.location.href = `https://${env.hostname}/activate-membership`;
    },
  },
  social: {
    text: 'Let’s Make It Official!',
    CTA: 'START MEMBERSHIP',
    onClick: () => {
      window.location.href = `https://${env.hostname}/subscribe-today`;
    },
  },
  CANCELED: {
    text: 'Come Back Today!',
    CTA: 'REACTIVATE NOW',
    onClick: handleClickCancelledReactivate,
  },
  CANCELLED_WITH_ZERO_BOXES: {
    text: 'Come Back Today!',
    CTA: 'REACTIVATE NOW',
    onClick: handleClickCancelledReactivate,
  },
  EXPIRED: {
    text: 'Come Back Today!',
    CTA: 'REACTIVATE NOW',
    onClick: handleClickExpiredReactivate,
  },
};

const DateSection = ({ label, value, dataTestId }) => (
  <div
    style={value === 'N/A' ? { opacity: 0.6 } : {}}
    className={styles.activation}
  >
    <label htmlFor="date" data-testid={dataTestId}>
      {label}
      <span id="date">{value}</span>
    </label>
  </div>
);

DateSection.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  dataTestId: PropTypes.string,
};

DateSection.defaultProps = {
  dataTestId: null,
};

export const MEMBER_SINCE_LABEL = 'Member Since:';

const formatDate = (date) => moment(date).format('LL');

const Profile = ({
  isDesktop,
  firstName,
  lastName,
  activationDate,
  subscription,
  subscriptionState,
  avatarUrl,
  uploadAvatar,
  remainingBoxes,
  displayRemainingBoxes,
  nextRenewal,
  isSocialUser,
  isClubMember,
}) => {
  const inputFile = useRef(null);
  const location = useLocation();
  const [mobileOnMyAccountPage, setMobileOnMyAccountPage] = useState();

  const { year, season, renewalDate } = nextRenewal;
  const { SUBSCRIPTION_STATE, COLOR } =
    getUserSubscriptionStatus(subscriptionState);
  const subscriptionCopy =
    SUBSCRIPTION_COPY[subscriptionState] || SUBSCRIPTION_COPY[subscription];
  const nextRenewalDate = renewalDate ? formatDate(renewalDate) : 'N/A';
  const nextRenewalSeason =
    season && year ? `${startCase(lowerCase(season))} ${year}` : 'N/A';

  const subscriptionName = isClubMember ? 'Alt-Sub' : subscription;

  const openFileDialog = () => {
    inputFile.current.click();
  };

  const fileChangedHandler = (e) => {
    const formData = new FormData();
    formData.append('file', e.target.files[0]);
    uploadAvatar(formData);
  };

  useEffect(() => {
    const isMobileOnMyAccountPage =
      !isDesktop &&
      !(
        location.pathname.includes('orders') ||
        location.pathname.includes('invoice-history') ||
        location.pathname.includes('personal-survey') ||
        location.pathname.includes('rate-and-review')
      );
    setMobileOnMyAccountPage(isMobileOnMyAccountPage);
  }, [location, isDesktop]);

  return (
    <div className={styles.topContainer}>
      <div className={styles.desktopContainer}>
        <input
          ref={inputFile}
          type="file"
          accept="image/jpeg, image/png"
          className={styles.fileDialog}
          onChange={fileChangedHandler}
        />
        {isDesktop && (
          <ProfileImage avatarUrl={avatarUrl} openFileDialog={openFileDialog} />
        )}
        <div
          className={classNames(styles.container, {
            [styles.socialContainer]: isSocialUser,
          })}
          data-testid="profile-container"
        >
          <div
            className={styles.name}
            data-testid="name-text"
          >{`Hello, ${firstName}!`}</div>
          {!isSocialUser && (
            <>
              {!isClubMember && (
                <div className={styles.statusContainer}>
                  <div
                    className={styles.subscription}
                    data-testid="subscription-type"
                  >
                    {SUBSCRIPTION_STATE === SUBSCRIPTION_STATUS.EXPIRED ? (
                      <p>
                        Guest <span className={styles.lowerCase}> pass</span>
                      </p>
                    ) : (
                      `${subscriptionName} Member`
                    )}
                  </div>
                  <div
                    className={classNames(
                      styles.subscriptionState,
                      styles[COLOR]
                    )}
                    data-testid="subscription-state"
                  >
                    {SUBSCRIPTION_STATE}
                  </div>
                </div>
              )}
              <div className={styles.activationContainer}>
                {!isClubMember && (
                  <>
                    {renewalDate ? (
                      <DateSection
                        label="Next Billing Date: "
                        value={nextRenewalDate}
                      />
                    ) : (
                      <DateSection
                        label="Next Billing Season: "
                        value={nextRenewalSeason}
                        dataTestId="next-billing-season-text"
                      />
                    )}
                  </>
                )}
                {activationDate && (
                  <DateSection
                    label={`${MEMBER_SINCE_LABEL} `}
                    value={formatDate(activationDate)}
                    dataTestId="activation-date-text"
                  />
                )}
                {!isClubMember && (
                  <a
                    href="https://support.fabfitfun.com/en/articles/4-billing-shipping-schedule"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="billing-shipping-link"
                  >
                    Billing / Shipping Schedule
                  </a>
                )}
              </div>
            </>
          )}
        </div>
        {displayRemainingBoxes && (
          <RemainingBoxes remainingBoxes={remainingBoxes} />
        )}
        {subscriptionCopy && isDesktop && (
          <div className={styles.desktopReactivate}>
            <div className={styles.left} />
            <div className={styles.reactivate}>
              <h3>{subscriptionCopy.text}</h3>
              <Button onClick={() => subscriptionCopy.onClick(subscription)}>
                {subscriptionCopy.CTA}
              </Button>
            </div>
            <div className={styles.right} />
          </div>
        )}
      </div>
      {subscriptionCopy && !isDesktop && (
        <div className={styles.reactivate}>
          <h3>{subscriptionCopy.text}</h3>
          <Button onClick={() => subscriptionCopy.onClick(subscription)}>
            {subscriptionCopy.CTA}
          </Button>
        </div>
      )}
      {mobileOnMyAccountPage && (
        <div className={styles.imageSection}>
          <div className={styles.header}>Account Details</div>
          <ProfileImage avatarUrl={avatarUrl} openFileDialog={openFileDialog} />
          <div className={styles.fullname}>{`${firstName} ${lastName}`}</div>
          <div className={styles.buttonsContainer}>
            <button type="button" onClick={openFileDialog}>
              Update Photo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
